import { SampleImageRenderer } from "../../SampleImageRenderer"
import { ImageWidget } from "../CustomWidgets"

/**
 * Configuration data for the Businesses component
 * Contains column definitions, UI schemas, and image processing functions
 */
export const configData = {
  getListLocation(id: any) {
    return ["businesses"]
  },
  EDIT_TITLE: "Edit Business",
  ADD_TITLE: "Add Business",
  LOADING_MESSAGE: "Loading Business...",
  TITLE: "Businesses",
  IMAGES_BUCKET: "businessesImages",

  columnsProperties: [
    { hidden: true, name: "id", label: "Id" },
    { required: false, name: "disabled", default: false, label: "Disabled", type: "boolean" },

    { required: true, name: "name", label: "Name" },
    { required: true, name: "order", label: "Order" },

    { required: true, name: "phone", label: "Phone" },
    { required: true, name: "email", label: "Email" },
    { required: true, name: "description", label: "Description" },
    { required: false, name: "websiteIPs", label: "Website IPs" },
    {
      hidden: false,
      required: false,
      name: "icon",
      options: {
        customBodyRender: SampleImageRenderer(),
      },
      label: "Icon",
    },

    {
      hidden: false,
      type: "array",
      required: false,
      name: "storefrontImages",
      label: "Storefront Images",
      options: {
        customBodyRender: SampleImageRenderer(),
      },
      items: {
        type: "string",
      },
    },
    {
      hidden: false,
      type: "array",
      required: false,
      name: "insideImages",
      label: "Inside Images",
      options: {
        customBodyRender: SampleImageRenderer(),
      },
      items: {
        type: "string",
      },
    },
    { required: true, name: "language", label: "Language" },
    { required: true, name: "country", label: "Country" },
    { required: false, name: "state", label: "State" },
    { required: false, name: "city", label: "City" },
    { required: false, name: "street", label: "Street" },
    { required: false, name: "county", label: "County" },
    { required: false, name: "zipCode", label: "Zip Code" },
  ],

  uiSchema: {
    icon: {
      "ui:widget": ImageWidget,
    },

    description: {
      "ui:widget": "textarea",
    },
    disabled: {
      "ui:widget": "radio",
    },
    storefrontImages: {
      items: {
        "ui:widget": ImageWidget,
      },
    },
    insideImages: {
      items: {
        "ui:widget": ImageWidget,
      },
    },
  },

  /**
   * Uploads storefront images for a business
   * @param formData - Form data containing storefront images
   * @param handleUpload - Upload function
   * @param id - Business ID
   * @returns Processed form data with uploaded image URLs
   */
  async uploadStorefrontImages(
    formData: { storefrontImages: any[] },
    handleUpload: (arg0: any, arg1: string, arg2: any) => any,
    id: any,
  ) {
    if (formData?.storefrontImages?.length > 0) {
      const imagesURLs = await Promise.all(
        formData.storefrontImages
          .map((image: any) => {
            return handleUpload(id, this.IMAGES_BUCKET, image)
          })
          .filter((x: any) => !!x),
      )
      formData.storefrontImages = imagesURLs
    }
    return formData
  },

  /**
   * Uploads inside images for a business
   * @param formData - Form data containing inside images
   * @param handleUpload - Upload function
   * @param id - Business ID
   * @returns Processed form data with uploaded image URLs
   */
  async uploadInsideImages(
    formData: { insideImages: any[] },
    handleUpload: (arg0: any, arg1: string, arg2: any) => any,
    id: any,
  ) {
    if (formData?.insideImages?.length > 0) {
      const imagesURLs = await Promise.all(
        formData.insideImages
          .map((image: any) => {
            return handleUpload(id, this.IMAGES_BUCKET, image)
          })
          .filter((x: any) => !!x),
      )
      formData.insideImages = imagesURLs
    }
    return formData
  },

  /**
   * Uploads icon image for a business
   * @param formData - Form data containing icon image
   * @param handleUpload - Upload function
   * @param id - Business ID
   * @returns Processed form data with uploaded icon URL
   */
  async uploadIconImage(
    formData: { icon: any },
    handleUpload: (arg0: any, arg1: string, arg2: any) => any,
    id: any,
  ) {
    if (formData.icon) {
      const icon = await handleUpload(id, this.IMAGES_BUCKET, formData.icon)
      formData.icon = icon
    }
    return formData
  },

  /**
   * Main image processor for business data
   * Handles all image uploads (storefront, inside, and icon images)
   * @param formData - Form data to process
   * @param handleUpload - Upload function
   * @param id - Business ID
   * @returns Processed form data with all images uploaded
   */
  async imageProcessor(formData: any, handleUpload: any, id: any) {
    formData = await this.uploadStorefrontImages(formData, handleUpload, id)
    formData = await this.uploadInsideImages(formData, handleUpload, id)
    formData = await this.uploadIconImage(formData, handleUpload, id)

    return formData
  },

  /**
   * Row click handler for business list navigation
   * @param list - List of businesses
   * @param navigate - Navigation function
   * @returns Click handler function
   */
  onRowClick: (
    list: { [x: string]: any },
    navigate: (arg0: string, arg1: { state: any }) => void,
  ) => {
    return (_rowData: any, rowMeta: { dataIndex: string | number }) => {
      const state = list[rowMeta.dataIndex]
      const id = state.id
      navigate(`/businesses/${id}`, { state })
    }
  },
}